import { useEffect, useMemo, useState } from "react";
import { collection, collectionGroup, doc, onSnapshot, orderBy, query, updateDoc, where } from "firebase/firestore";
import { editStamp } from "../../lib/editStamp";
import { Link } from "wouter";
import { Pin, PinOff } from "lucide-react";
import { paths, type Department, type Invoice, type Job } from "@clevis/shared-types";
import { Button, Card, Input, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import {
  BUCKETS,
  BUCKET_HINT,
  BUCKET_LABEL,
  computePipeline,
  type Bucket,
  type PipelineJob,
} from "../../lib/pipeline";
import { textMatches } from "../../lib/filter";
import { useAuth } from "../../hooks/useAuth";
import { useMemberNames } from "../../hooks/useMemberNames";

// The pipeline: every root job in exactly one derived bucket from Scheduled
// to Paid. Tap a tile, work the list — complete, reschedule, chase the money
// — without leaving the page. Managers can pin tiles to their dashboard.

const money = (c: number) =>
  (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export function PipelinePage() {
  const { tenantId, user } = useAuth();
  const memberName = useMemberNames(tenantId);
  const [jobs, setJobs] = useState<Array<Job & { id: string }>>([]);
  const [invoices, setInvoices] = useState<Array<Invoice & { id: string }>>([]);
  const [customers, setCustomers] = useState<Record<string, string>>({});
  const [active, setActive] = useState<Bucket>("partially_complete");
  const [byTech, setByTech] = useState(false);
  const [search, setSearch] = useState("");
  const [pins, setPins] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [openParts, setOpenParts] = useState<Set<string>>(new Set());
  const [readyParts, setReadyParts] = useState<Array<{ id: string; jobId: string; description: string }>>([]);
  const [departments, setDepartments] = useState<Array<Department & { id: string }>>([]);
  const [queue, setQueue] = useState<string>("all"); // all | mine | <departmentId>

  useEffect(() => {
    if (!tenantId || !user) return;
    const subs = [
      onSnapshot(query(collection(db, paths.tenantJobs(tenantId)), orderBy("createdAt", "desc")), (snap) =>
        setJobs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Job) }))),
      ),
      onSnapshot(query(collection(db, paths.tenantInvoices(tenantId)), orderBy("createdAt", "desc")), (snap) =>
        setInvoices(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Invoice) }))),
      ),
      onSnapshot(collection(db, paths.tenantCustomers(tenantId)), (snap) => {
        const m: Record<string, string> = {};
        snap.docs.forEach((d) => (m[d.id] = (d.get("displayName") as string) ?? ""));
        setCustomers(m);
      }),
      onSnapshot(doc(db, paths.user(user.uid)), (snap) =>
        setPins((snap.get("pinnedPipelineTiles") as string[] | undefined) ?? []),
      ),
      // Every part still on order, company-wide → the waiting-parts bucket.
      onSnapshot(
        query(
          collectionGroup(db, "orderedItems"),
          where("tenantId", "==", tenantId),
          where("status", "==", "ordered"),
        ),
        (snap) => setOpenParts(new Set(snap.docs.map((d) => (d.get("jobId") as string) ?? ""))),
      ),
      // Staged procurement: planned parts whose trigger fired — order them now.
      onSnapshot(
        query(
          collectionGroup(db, "orderedItems"),
          where("tenantId", "==", tenantId),
          where("status", "==", "ready"),
        ),
        (snap) =>
          setReadyParts(
            snap.docs.map((d) => ({
              id: d.id,
              jobId: (d.get("jobId") as string) ?? "",
              description: (d.get("description") as string) ?? "part",
            })),
          ),
      ),
      onSnapshot(collection(db, paths.tenantDepartments(tenantId)), (snap) =>
        setDepartments(snap.docs.filter((d) => d.get("kind") !== "group").map((d) => ({ id: d.id, ...(d.data() as Department) }))),
      ),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId, user]);

  const myDeptIds = useMemo(
    () => new Set(departments.filter((d) => user && d.managerUids.includes(user.uid)).map((d) => d.id)),
    [departments, user],
  );

  const queuedJobs = useMemo(() => {
    if (queue === "all") return jobs;
    if (queue === "mine") return jobs.filter((j) => j.departmentId && myDeptIds.has(j.departmentId));
    return jobs.filter((j) => j.departmentId === queue);
  }, [jobs, queue, myDeptIds]);

  const buckets = useMemo(() => computePipeline(queuedJobs, invoices, openParts), [queuedJobs, invoices, openParts]);

  const rows = useMemo(() => {
    const list = buckets.get(active) ?? [];
    if (!search.trim()) return list;
    return list.filter((p) =>
      textMatches(
        search,
        p.job.title,
        p.job.address,
        p.job.note,
        customers[p.job.customerId ?? ""] ?? "",
        ...p.job.assignedUids.map(memberName),
      ),
    );
  }, [buckets, active, search, customers, memberName]);

  const grouped = useMemo(() => {
    if (!byTech) return [["", rows]] as Array<[string, PipelineJob[]]>;
    const m = new Map<string, PipelineJob[]>();
    for (const r of rows) {
      const name = r.job.assignedUids[0] ? memberName(r.job.assignedUids[0]) : "Unassigned";
      (m.get(name) ?? m.set(name, []).get(name)!).push(r);
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [rows, byTech, memberName]);

  async function togglePin(bucket: Bucket) {
    if (!tenantId || !user) return;
    const next = pins.includes(bucket) ? pins.filter((p) => p !== bucket) : [...pins, bucket];
    await call("updateMemberSettings", { tenantId, uid: user.uid, patch: { pinnedPipelineTiles: next } }).catch(
      (err) => setMessage((err as Error).message),
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-bold">Pipeline</h1>
        {departments.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {[
              { id: "all", label: "All" },
              ...(myDeptIds.size > 0 ? [{ id: "mine", label: "My queue" }] : []),
              ...departments.map((d) => ({ id: d.id, label: d.name })),
            ].map((c) => (
              <button
                key={c.id}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs",
                  queue === c.id
                    ? "border-brandorange bg-brandorange/10 text-brandorange-dark"
                    : "border-steel/30 text-steel hover:text-ink",
                )}
                onClick={() => setQueue(c.id)}
              >
                {c.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {message ? <p className="text-xs text-danger">{message}</p> : null}

      {readyParts.length > 0 ? (
        <Card className="border-brandorange/50">
          <div className="mb-1 text-sm font-semibold text-brandorange-dark">
            Ready to order ({readyParts.length})
          </div>
          <div className="space-y-1">
            {readyParts.slice(0, 8).map((p) => (
              <Link
                key={p.id}
                href={`/jobs/${p.jobId}`}
                className="block text-sm text-ink hover:text-brandorange"
              >
                → {p.description}
                <span className="ml-1 text-xs text-steel">
                  {jobs.find((j) => j.id === p.jobId)?.title ?? ""}
                </span>
              </Link>
            ))}
            {readyParts.length > 8 ? (
              <p className="text-xs text-steel">+{readyParts.length - 8} more</p>
            ) : null}
          </div>
        </Card>
      ) : null}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {BUCKETS.map((b) => {
          const count = buckets.get(b)?.length ?? 0;
          const pinned = pins.includes(b);
          return (
            <button
              key={b}
              onClick={() => setActive(b)}
              className={cn(
                "relative rounded-xl border px-3 py-2 text-left transition-colors",
                active === b ? "border-brandorange bg-white" : "border-steel/25 bg-white hover:border-brandorange/60",
              )}
              title={BUCKET_HINT[b]}
            >
              <span
                className="absolute right-1.5 top-1.5 text-steel/60 hover:text-brandorange"
                title={pinned ? "Unpin from dashboard" : "Pin to dashboard"}
                onClick={(e) => {
                  e.stopPropagation();
                  void togglePin(b);
                }}
              >
                {pinned ? <Pin size={13} className="text-brandorange" /> : <PinOff size={13} />}
              </span>
              <div className={cn("text-xl font-bold", count > 0 ? "text-ink" : "text-steel/60")}>{count}</div>
              <div className="text-[11px] leading-tight text-steel">{BUCKET_LABEL[b]}</div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <Input
          placeholder={`Filter ${BUCKET_LABEL[active].toLowerCase()}…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          className={cn(
            "shrink-0 rounded-lg border px-3 py-2 text-xs",
            byTech ? "border-brandorange text-brandorange-dark" : "border-steel/30 text-steel hover:text-ink",
          )}
          onClick={() => setByTech((v) => !v)}
        >
          Group by tech
        </button>
      </div>

      {grouped.map(([group, list]) => (
        <div key={group}>
          {group ? (
            <p className="mb-1 mt-2 text-xs font-semibold text-steel">
              {group} · {list.length}
            </p>
          ) : null}
          <Card className="p-0">
            {list.map((p) => (
              <PipelineRow
                key={p.job.id}
                p={p}
                tenantId={tenantId!}
                customerName={customers[p.job.customerId ?? ""] ?? ""}
                memberName={memberName}
                onMessage={setMessage}
              />
            ))}
            {list.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-steel">
                {search ? "No matches here." : `Nothing in ${BUCKET_LABEL[active].toLowerCase()}. 🎉`}
              </p>
            ) : null}
          </Card>
        </div>
      ))}
    </div>
  );
}

function PipelineRow({
  p,
  tenantId,
  customerName,
  memberName,
  onMessage,
}: {
  p: PipelineJob;
  tenantId: string;
  customerName: string;
  memberName: (uid: string) => string;
  onMessage: (m: string) => void;
}) {
  const { user } = useAuth();
  const [resched, setResched] = useState(false);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("08:00");
  const [busy, setBusy] = useState<string | null>(null);
  const j = p.job;
  const openInvoice = p.invoices.find((i) => i.status === "sent" || i.status === "partially_paid");
  const billedTotal = p.invoices.reduce((s, i) => s + i.totalCents, 0);
  const paidTotal = p.invoices.reduce((s, i) => s + i.amountPaidCents, 0);

  async function patch(data: Record<string, unknown>) {
    await updateDoc(doc(db, paths.tenantJob(tenantId, j.id)), { ...data, ...editStamp(user) });
  }

  async function markDone() {
    setBusy("done");
    try {
      await patch({ status: "done" });
    } finally {
      setBusy(null);
    }
  }

  async function reschedule() {
    if (!date) return;
    setBusy("resched");
    try {
      const startAt = new Date(`${date}T${time}`).getTime();
      await patch({ startAt, endAt: startAt + 2 * 3600_000, status: "scheduled" });
      setResched(false);
    } finally {
      setBusy(null);
    }
  }

  async function textPayLink() {
    if (!openInvoice) return;
    setBusy("text");
    try {
      await call("sendInvoiceText", { tenantId, invoiceId: openInvoice.id });
      onMessage(`Pay link texted for invoice #${openInvoice.number}.`);
    } catch (err) {
      onMessage((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="border-b border-steel/10 px-4 py-2 last:border-0">
      <div className="flex items-center justify-between gap-2">
        <Link href={`/jobs/${j.id}`} className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-ink hover:text-brandorange">{j.title}</div>
          <div className="truncate text-xs text-steel">
            {[
              customerName,
              j.startAt ? new Date(j.startAt).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "no date",
              j.assignedUids[0] ? memberName(j.assignedUids[0]) : "unassigned",
              p.children.length > 0
                ? `${p.children.filter((c) => c.status === "done").length}/${p.children.length} visits`
                : "",
              billedTotal > 0 ? `${money(paidTotal)} of ${money(billedTotal)}` : "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </Link>
        <div className="flex shrink-0 items-center gap-1.5">
          {j.status !== "done" ? (
            <Button variant="ghost" className="px-2 py-1 text-xs" busy={busy === "done"} onClick={() => void markDone()}>
              Done
            </Button>
          ) : null}
          {j.status !== "done" ? (
            <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setResched((v) => !v)}>
              Reschedule
            </Button>
          ) : null}
          {p.bucket === "done_unbilled" ? (
            <Link href="/invoices" className="text-xs text-brandorange-dark hover:text-brandorange">
              Invoice →
            </Link>
          ) : null}
          {openInvoice ? (
            <Button variant="ghost" className="px-2 py-1 text-xs" busy={busy === "text"} onClick={() => void textPayLink()}>
              Text pay link
            </Button>
          ) : null}
        </div>
      </div>
      {resched ? (
        <div className="mt-1.5 flex items-center gap-2">
          <Input type="date" className="w-36 py-1 text-xs" value={date} onChange={(e) => setDate(e.target.value)} />
          <Input type="time" className="w-28 py-1 text-xs" value={time} onChange={(e) => setTime(e.target.value)} />
          <Button className="px-3 py-1 text-xs" busy={busy === "resched"} disabled={!date} onClick={() => void reschedule()}>
            Save
          </Button>
        </div>
      ) : null}
    </div>
  );
}
